// material-ui
import { styled } from '@mui/material/styles';

// project imports
import { MenuOrientation } from 'config';
import { drawerWidth } from 'store/constant';

// ⚠ Every top offset below is MEASURED, not written. Berry repeated 88 (vertical menu) and 135 (horizontal)
// across five branches of this one file — numbers that only ever described the height Berry's own toolbar
// happened to come out at. A taller header (a tenant logo, a browser font-size setting) overflowed the
// reserve and landed on top of the breadcrumbs and the page's first button, because the reserve did not
// follow. `contentTopVertical` / `contentTopHorizontal` read the AppBar's real height at paint time and fall
// back to Berry's original numbers when nothing has measured. See shellOffsets.ts.
import { contentTopHorizontal, contentTopVertical, viewportBelowAppBar } from './shellOffsets';

interface MainStyleProps {
  open: boolean;
  menuOrientation: MenuOrientation;
  borderRadius: number;
}

/** The one place the orientation picks an offset; five branches used to repeat the ternary. */
const contentTop = (menuOrientation: MenuOrientation): string =>
  menuOrientation === MenuOrientation.HORIZONTAL ? contentTopHorizontal : contentTopVertical;

// ==============================|| MAIN LAYOUT - STYLED ||============================== //

const MainContentStyled = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'menuOrientation' && prop !== 'borderRadius'
})<MainStyleProps>(({ theme, open, menuOrientation, borderRadius }) => ({
  backgroundColor: theme.vars.palette.grey[100],

  ...theme.applyStyles('dark', {
    backgroundColor: theme.vars.palette.dark[800]
  }),
  minWidth: '1%',
  width: '100%',
  minHeight: viewportBelowAppBar(),
  flexGrow: 1,
  padding: 20,
  marginTop: contentTopVertical,
  marginRight: 20,
  borderRadius: `${borderRadius}px`,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  ...(!open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter + 200
    }),
    [theme.breakpoints.up('md')]: {
      marginLeft: menuOrientation === MenuOrientation.VERTICAL ? -(drawerWidth - 72) : 20,
      width: `calc(100% - ${drawerWidth}px)`,
      marginTop: contentTop(menuOrientation)
    }
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.shorter + 200
    }),
    marginLeft: menuOrientation === MenuOrientation.HORIZONTAL ? 20 : 0,
    marginTop: contentTop(menuOrientation),
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.up('md')]: {
      marginTop: contentTop(menuOrientation)
    }
  }),
  // ⚠ Below md the horizontal bar is not rendered at all (MainLayout gates it on `!downMD`), so the content
  // offsets by the AppBar alone whatever the persisted orientation says. This block is LAST on purpose: it
  // has to win over the `open` branch above, which is not itself scoped to a breakpoint.
  [theme.breakpoints.down('md')]: {
    marginLeft: 20,
    padding: 16,
    marginTop: contentTopVertical,
    ...(!open && {
      width: `calc(100% - ${drawerWidth}px)`
    })
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 10,
    marginRight: 10
  }
}));

export default MainContentStyled;
